import { isError } from '@nicolaudie-group/ng-ui-kit/helpers/isStatus'

import pinia from '@/stores/pinia'
import useProfileStore, {
  profileStatusKey,
  brandsStatusKey,
  constantsStatusKey,
  profileKey,
} from '@/stores/profile.store'
import useUserStore, { userStatus } from '@/stores/user.store'
import useSettingsStore, { groupsStatusKey } from '@/stores/settings.store'

/**
 * Getters that return wether a certain required set of data failed to load
 */
const failed = {
  /**
   * True if the profile the profile loading failed in the profile store
   */
  get profile() {
    const profileStore = useProfileStore(pinia)
    return isError(profileStore[profileStatusKey]) && !profileStore[profileKey]
  },
  /**
   * True if the user loading failed in the user store
   */
  get user() {
    const userStore = useUserStore(pinia)
    return isError(userStore[userStatus])
  },
  /**
   * True if any of the required constants failed to load in their
   * relevant store
   */
  get constants() {
    const settingsStore = useSettingsStore(pinia)
    const profileStore = useProfileStore(pinia)
    return (
      isError(settingsStore[groupsStatusKey])
      || isError(profileStore[brandsStatusKey])
      || isError(profileStore[constantsStatusKey])
    )
  },
  /**
   * True if the translations failed to load
   */
  get translation() {
    const userStore = useUserStore(pinia)
    return isError(userStore.translationStatus)
  },
}

/**
 * Hook stored on the routes meta, and called by the `App` component,
 * to check wether any of the required resources for the "Editor" view
 * failed to load
 * @returns {Boolean} Wether any of the required resources failed to load or not
 */
export const editor = () => (
  failed.constants
  || failed.user
  || failed.translation
  || failed.profile
)

/**
 * Hook stored on the routes meta, and called by the `App` component,
 * to check wether any of the required resources for the "Home" view
 * failed to load
 * @returns {Boolean} Wether any of the required resources failed to load or not
 */
export const home = () => (
  failed.constants
  || failed.user
  || failed.translation
)

/**
 * Hook stored on the routes meta, and called by the `App` component,
 * to check wether any of the required resources for the "Create" view
 * failed to load
 * @returns {Boolean} Wether any of the required resources failed to load or not
 */
export const create = () => (
  failed.constants
  || failed.user
  || failed.translation
)

/**
 * Hook stored on the routes meta, and called by the `App` component,
 * to bypass checking if any of the required resources failed to load
 * @returns {Boolean} Always `false`
 */
export const bypass = () => false
